;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();export type NotificationMetadataValue =
  | string
  | number
  | boolean
  | null
  | NotificationMetadataValue[]
  | { [key: string]: NotificationMetadataValue };

export type NotificationMetadata = { [key: string]: NotificationMetadataValue };

export interface NotificationRoutingFields {
  key?: string | null;
  subcategory?: string | null;
  metadata?: NotificationMetadata | null;
}

export interface NotificationApiRoutingFields {
  key?: string | null;
  notification_key?: string | null;
  subcategory?: string | null;
  metadata?: NotificationMetadata | null;
}

export const PAYROLL_TO_RUN_PATH = '/paas-management';

/** Query value when notification metadata has no `country_id`. */
export const PAYROLL_TO_RUN_COUNTRY_ID_UNDEFINED = 'undefined';

const PAYROLL_TO_RUN_REVIEW_NOTIFICATION_KEYS = new Set([
  'final_client_review',
  'final_client_review_revised',
  'final_client_review_reminder',
]);

const PAYROLL_PERIOD_MM_YYYY_PATTERN = /^\d{2}\/\d{4}$/;
const PAYROLL_PERIOD_LOOSE_MM_YYYY_PATTERN = /^(\d{1,2})\/(\d{4})$/;

/** Payroll notification metadata period fields (admin + client apps). */
const NOTIFICATION_PAYROLL_PERIOD_METADATA_FIELDS = {
  /** e.g. `"03/2027"` */
  periodMmYyyyKey: 'period',
  /** Numeric month when `period` is absent (e.g. admin `final_client_*` notifications). */
  periodMonthKeys: ['period_month'] as const,
  /** Numeric year when `period` is absent. */
  periodYearKeys: ['period_year'] as const,
};

function getMetadataNumber(
  metadata: NotificationMetadata | null | undefined,
  key: string
): number | undefined {
  const value = metadata?.[key];

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number.parseInt(value.trim(), 10);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return undefined;
}

function getFirstMetadataNumber(
  metadata: NotificationMetadata | null | undefined,
  keys: readonly string[]
): number | undefined {
  for (const key of keys) {
    const value = getMetadataNumber(metadata, key);
    if (value != null) {
      return value;
    }
  }

  return undefined;
}

function formatNotificationPeriodMmYyyy(month: number, year: number): string {
  return `${String(month).padStart(2, '0')}/${year}`;
}

/** Accepts `M/yyyy` or `MM/yyyy` and returns canonical `MM/yyyy` for URL `date` query values. */
function normalizePayrollPeriodMmYyyy(raw: string): string | null {
  const trimmed = raw.trim();
  if (PAYROLL_PERIOD_MM_YYYY_PATTERN.test(trimmed)) {
    return trimmed;
  }

  const match = PAYROLL_PERIOD_LOOSE_MM_YYYY_PATTERN.exec(trimmed);
  if (!match) {
    return null;
  }

  const [, monthStr, yearStr] = match;
  if (monthStr === undefined || yearStr === undefined) {
    return null;
  }

  const month = Number.parseInt(monthStr, 10);
  const year = Number.parseInt(yearStr, 10);
  if (!Number.isFinite(month) || !Number.isFinite(year) || month < 1 || month > 12) {
    return null;
  }

  return formatNotificationPeriodMmYyyy(month, year);
}

/**
 * Resolves payroll period from notification metadata as `MM/yyyy` (URL `date` query format).
 * Uses `metadata.period`, or `metadata.period_month` + `metadata.period_year`.
 */
export function resolveNotificationPeriodMmYyyy(
  metadata: NotificationMetadata | null | undefined
): string | null {
  const periodMmYyyy = getNotificationMetadataString(
    metadata,
    NOTIFICATION_PAYROLL_PERIOD_METADATA_FIELDS.periodMmYyyyKey
  );
  if (periodMmYyyy) {
    const normalizedPeriod = normalizePayrollPeriodMmYyyy(periodMmYyyy);
    if (normalizedPeriod) {
      return normalizedPeriod;
    }
  }

  const alertDate = getNotificationMetadataString(metadata, 'date');
  if (alertDate) {
    const normalizedDate = normalizePayrollPeriodMmYyyy(alertDate);
    if (normalizedDate) {
      return normalizedDate;
    }
  }

  const month = getFirstMetadataNumber(
    metadata,
    NOTIFICATION_PAYROLL_PERIOD_METADATA_FIELDS.periodMonthKeys
  );
  const year = getFirstMetadataNumber(
    metadata,
    NOTIFICATION_PAYROLL_PERIOD_METADATA_FIELDS.periodYearKeys
  );

  if (month != null && year != null && month >= 1 && month <= 12) {
    return formatNotificationPeriodMmYyyy(month, year);
  }

  return null;
}

export function getNotificationMetadataString(
  metadata: NotificationMetadata | null | undefined,
  key: string
): string | null {
  const value = metadata?.[key];

  if (typeof value === 'number') {
    return String(value);
  }

  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }

  return null;
}

function resolveNotificationSubcategory(
  notification: Pick<NotificationRoutingFields, 'subcategory' | 'metadata'>
): string | null {
  const topLevel = notification.subcategory?.trim();
  if (topLevel) {
    return topLevel;
  }

  return getNotificationMetadataString(notification.metadata, 'subcategory');
}

export function resolveNotificationKey(
  notification: Pick<NotificationRoutingFields, 'key' | 'metadata'>
): string | null {
  const topLevel = notification.key?.trim();
  if (topLevel) {
    return topLevel;
  }

  return getNotificationMetadataString(notification.metadata, 'key');
}

function isPayrollToRunSubcategory(notification: NotificationRoutingFields): boolean {
  return resolveNotificationSubcategory(notification) === 'payroll_to_run';
}

export function isPayrollToRunReviewNotification(notification: NotificationRoutingFields): boolean {
  if (isPayrollToRunSubcategory(notification)) {
    return true;
  }

  const key = resolveNotificationKey(notification);
  return key != null && PAYROLL_TO_RUN_REVIEW_NOTIFICATION_KEYS.has(key);
}

/** Builds `/paas-management` href from payroll-to-run metadata (`country_id`, `date` / period fields). */
export function buildPayrollToRunHrefFromMetadata(
  metadata: NotificationMetadata | null | undefined
): string {
  // TODO: Once all payroll-to-run notifications include `country_id` in metadata, remove the logic that
  // treats a missing `country_id` as null and falls back to `country_id=undefined`.
  const countryIdValue = metadata?.country_id;
  let countryId: number | null = null;

  if (typeof countryIdValue === 'number' && Number.isFinite(countryIdValue) && countryIdValue > 0) {
    countryId = countryIdValue;
  } else if (typeof countryIdValue === 'string' && countryIdValue.trim()) {
    const parsed = Number.parseInt(countryIdValue, 10);
    if (Number.isFinite(parsed) && parsed > 0) {
      countryId = parsed;
    }
  }

  const params = new URLSearchParams({
    country_id: countryId != null ? String(countryId) : PAYROLL_TO_RUN_COUNTRY_ID_UNDEFINED,
  });

  const periodMmYyyy = resolveNotificationPeriodMmYyyy(metadata);
  if (periodMmYyyy) {
    params.set('date', periodMmYyyy);
  }

  return `${PAYROLL_TO_RUN_PATH}?${params.toString()}`;
}

/** Payroll-to-run — `country_id` query (`undefined` when metadata has no country id). */
export function getPayrollToRunNotificationHref(
  notification: NotificationRoutingFields
): string | null {
  if (!isPayrollToRunReviewNotification(notification)) {
    return null;
  }

  return buildPayrollToRunHrefFromMetadata(notification.metadata);
}

export function readApiNotificationKey(
  apiNotification: NotificationApiRoutingFields
): string | null {
  if (apiNotification.key?.trim()) {
    return apiNotification.key.trim();
  }

  const notificationKey = apiNotification.notification_key?.trim();
  if (notificationKey) {
    return notificationKey;
  }

  return getNotificationMetadataString(apiNotification.metadata, 'key');
}

export function readApiNotificationSubcategory(
  apiNotification: NotificationApiRoutingFields
): string | null {
  if (apiNotification.subcategory?.trim()) {
    return apiNotification.subcategory.trim();
  }

  return getNotificationMetadataString(apiNotification.metadata, 'subcategory');
}
